import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import { LoadingSpinner } from "../../../components/ui";
import type { AnexoChamado } from "../../../types/portalCliente";
import { abrirAnexo, baixarAnexo, mensagemDeErro } from "./api";
import { formatarData, formatarTamanho } from "./severidade";

interface VisualizadorAnexoProps {
  idChamado: number;
  /** Anexos exibíveis, na ordem da lista: permite navegar sem fechar. */
  anexos: AnexoChamado[];
  indiceInicial: number;
  aoFechar: () => void;
}

/**
 * Anexo aberto na própria tela. O conteúdo vem pelo cliente autenticado e vira
 * um object URL — endereço direto não funcionaria, porque a sessão é por
 * cabeçalho. O URL é revogado a cada troca e ao fechar: sem isso o blob fica
 * na memória da aba até a página recarregar.
 */
export const VisualizadorAnexo = ({
  idChamado,
  anexos,
  indiceInicial,
  aoFechar,
}: VisualizadorAnexoProps) => {
  const [indice, setIndice] = useState(indiceInicial);
  const [conteudo, setConteudo] = useState<{ url: string; tipo: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const anexo = anexos[indice];

  useEffect(() => {
    let cancelado = false;
    let urlAtual: string | null = null;

    setConteudo(null);
    setErro(null);

    abrirAnexo(idChamado, anexo)
      .then((resultado) => {
        if (cancelado) {
          URL.revokeObjectURL(resultado.url);
          return;
        }
        urlAtual = resultado.url;
        setConteudo(resultado);
      })
      .catch((e) => {
        if (!cancelado) setErro(mensagemDeErro(e, "Não foi possível abrir o anexo."));
      });

    return () => {
      cancelado = true;
      if (urlAtual) URL.revokeObjectURL(urlAtual);
    };
  }, [idChamado, anexo]);

  // Esc fecha; setas trocam de anexo, como em qualquer visualizador.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") aoFechar();
      if (e.key === "ArrowLeft") setIndice((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIndice((i) => Math.min(anexos.length - 1, i + 1));
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aoFechar, anexos.length]);

  const ehImagem = conteudo?.tipo.startsWith("image/");
  const ehPdf = conteudo?.tipo === "application/pdf";

  // Vai para o body por necessidade, não por estilo: os cartões do detalhe têm
  // backdrop-blur, e backdrop-filter cria bloco de contenção — dentro dele,
  // `position: fixed` passa a medir o cartão, e a tela cheia virava um
  // quadradinho no canto do painel de anexos.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={anexo.denominacao ?? "Anexo"}
      onClick={aoFechar}
    >
      <header
        className="flex items-center gap-3 px-4 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{anexo.denominacao ?? "Anexo"}</p>
          <p className="text-xs text-white/60">
            {formatarTamanho(anexo.tamanho)} · {formatarData(anexo.data)}
            {anexos.length > 1 && ` · ${indice + 1} de ${anexos.length}`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => baixarAnexo(idChamado, anexo)}
          className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-1.5 text-sm transition-colors hover:bg-white/20"
        >
          <Download className="h-4 w-4" />
          Baixar
        </button>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar"
          className="rounded-lg p-2 transition-colors hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 items-stretch gap-2 px-2 pb-4">
        {anexos.length > 1 && (
          <BotaoNavegacao
            direcao="anterior"
            desabilitado={indice === 0}
            aoClicar={() => setIndice((i) => i - 1)}
          />
        )}

        {/* items-stretch, e não items-center: o PDF ocupa a altura toda, e quem
            se centraliza é a imagem, pelo próprio m-auto. Centralizar aqui
            colapsava o iframe numa faixa de poucos pixels. */}
        <div
          className="flex min-h-0 flex-1 items-stretch justify-center overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {erro && (
            <p className="m-auto rounded-xl bg-white px-6 py-4 text-sm text-red-700">
              {erro}
            </p>
          )}

          {!erro && !conteudo && (
            <div className="m-auto">
              <LoadingSpinner />
            </div>
          )}

          {conteudo && ehImagem && (
            <img
              src={conteudo.url}
              alt={anexo.denominacao ?? "Anexo"}
              className="m-auto max-h-full max-w-full rounded-lg object-contain shadow-2xl"
            />
          )}

          {conteudo && ehPdf && (
            <iframe
              src={conteudo.url}
              title={anexo.denominacao ?? "Anexo"}
              className="h-full w-full rounded-lg bg-white shadow-2xl"
            />
          )}

          {conteudo && !ehImagem && !ehPdf && (
            <div className="m-auto rounded-xl bg-white px-6 py-5 text-center">
              <p className="text-sm text-gray-700">
                Este tipo de arquivo não abre no navegador.
              </p>
              <button
                type="button"
                onClick={() => baixarAnexo(idChamado, anexo)}
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Download className="h-4 w-4" />
                Baixar arquivo
              </button>
            </div>
          )}
        </div>

        {anexos.length > 1 && (
          <BotaoNavegacao
            direcao="proximo"
            desabilitado={indice === anexos.length - 1}
            aoClicar={() => setIndice((i) => i + 1)}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

const BotaoNavegacao = ({
  direcao,
  desabilitado,
  aoClicar,
}: {
  direcao: "anterior" | "proximo";
  desabilitado: boolean;
  aoClicar: () => void;
}) => (
  <button
    type="button"
    disabled={desabilitado}
    aria-label={direcao === "anterior" ? "Anexo anterior" : "Próximo anexo"}
    onClick={(e) => {
      e.stopPropagation();
      aoClicar();
    }}
    className="shrink-0 self-center rounded-full p-2 text-white transition-colors hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-transparent"
  >
    {direcao === "anterior" ? (
      <ChevronLeft className="h-6 w-6" />
    ) : (
      <ChevronRight className="h-6 w-6" />
    )}
  </button>
);

export default VisualizadorAnexo;
